// @ts-nocheck
import { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Settings,
  CreditCard,
  Shield,
  Users,
  Bell,
  UserCircle,
  Mic
} from 'lucide-react';

// Import settings components
import { PersonalSettings } from './settings/personal-settings';
import { VoiceSettings } from './settings/voice-settings';
import { GeneralSettings } from './settings/general-settings';
import { BillingSubscriptions } from './settings/billing-subscriptions';
import { SecurityPrivacy } from './settings/security-privacy';
import { UserManagement } from './user-management';
// ResendDiagnostic tab hidden - diagnostics available via edge function /debug-env
import { hasPermission, getRoleDisplayName, type UserRole } from '@saas-boilerplate/shared';

interface SettingsMenuItem {
  id: string;
  label: string;
  icon: any;
  description: string;
  badge?: string;
  requiredPermission: string;
  group: 'personal' | 'org';
}

const settingsMenuItems: SettingsMenuItem[] = [
  // ── Personal (your own account) ──
  {
    id: 'preferences',
    label: 'Preferences',
    icon: UserCircle,
    description: 'Your bot name, locale, working hours',
    requiredPermission: 'canAccessPersonalSettings',
    group: 'personal',
  },
  {
    id: 'notifications',
    label: 'Notifications',
    icon: Bell,
    description: 'How and when upstand.ai notifies you',
    requiredPermission: 'canAccessPersonalSettings',
    group: 'personal',
  },
  {
    id: 'voice',
    label: 'Voice',
    icon: Mic,
    description: 'Your voiceprint for speaker attribution',
    requiredPermission: 'canAccessPersonalSettings',
    group: 'personal',
  },
  {
    id: 'security',
    label: 'Security',
    icon: Shield,
    description: 'Your account security and sign-in',
    requiredPermission: 'canAccessSecuritySettings',
    group: 'personal',
  },
  // ── Organisation (workspace-wide; admins) ──
  {
    id: 'general',
    label: 'General',
    icon: Settings,
    description: 'Workspace settings and preferences',
    requiredPermission: 'canAccessGeneralSettings',
    group: 'org',
  },
  {
    id: 'billing',
    label: 'Billing & Subscriptions',
    icon: CreditCard,
    description: 'Manage plans, usage, and billing',
    requiredPermission: 'canAccessBillingSettings',
    group: 'org',
  },
  {
    id: 'users',
    label: 'User Management',
    icon: Users,
    description: 'Manage team members and permissions',
    requiredPermission: 'canAccessUserManagement',
    group: 'org',
  },
];

interface SettingsNavigationProps {
  mode: 'personal' | 'org';
  activeSettingsView: string;
  setActiveSettingsView: (view: string) => void;
  userProfile?: any;
  checkoutResult?: 'success' | 'canceled' | null;
  onCheckoutResultClose?: () => void;
}

export function SettingsNavigation({ mode, activeSettingsView, setActiveSettingsView, userProfile, checkoutResult, onCheckoutResultClose }: SettingsNavigationProps) {
  // Check permissions for each item
  const userRole = userProfile?.role as UserRole;
  const roleDisplayName = getRoleDisplayName(userRole || 'member');

  const renderSettingsContent = () => {
    // Check if user has permission to view this content
    const currentItem = settingsMenuItems.find(item => item.id === activeSettingsView);
    const hasAccess = currentItem ? hasPermission(userRole, currentItem.requiredPermission as any) : false;
    
    if (!hasAccess) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Access Restricted</h3>
            <p className="text-muted-foreground">
              You don't have permission to access this section.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              You are a {roleDisplayName}
            </p>
          </div>
        </div>
      );
    }

    switch (activeSettingsView) {
      case 'preferences':
        return <PersonalSettings section="personal" />;
      case 'notifications':
        return <PersonalSettings section="notifications" />;
      case 'voice':
        return <VoiceSettings />;
      case 'general':
        return <GeneralSettings />;
      case 'billing':
        return (
          <BillingSubscriptions
            checkoutResult={checkoutResult ?? null}
            onCheckoutResultClose={onCheckoutResultClose}
          />
        );
      case 'users':
        return <UserManagement />;
      case 'security':
        return <SecurityPrivacy />;
      default:
        return mode === 'personal' ? <PersonalSettings section="personal" /> : <GeneralSettings />;
    }
  };

  const renderItem = (item: SettingsMenuItem) => {
    const Icon = item.icon;
    const isActive = activeSettingsView === item.id;
    const hasAccess = hasPermission(userRole, item.requiredPermission as any);
    return (
      <Button
        key={item.id}
        variant={isActive ? 'secondary' : 'ghost'}
        className={`w-full justify-start h-auto p-3 ${!hasAccess ? 'opacity-50 cursor-not-allowed' : ''}`}
        onClick={() => hasAccess && setActiveSettingsView(item.id)}
        disabled={!hasAccess}
      >
        <Icon className="h-4 w-4 mr-3 flex-shrink-0" />
        <div className="flex-1 text-left">
          <div className="flex items-center justify-between">
            <span className={`font-medium ${!hasAccess ? 'text-muted-foreground' : ''}`}>
              {item.label}
            </span>
            {item.badge && hasAccess && (
              <Badge variant="secondary" className="ml-2 text-xs">
                {item.badge}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
        </div>
      </Button>
    );
  };

  return (
    <div className="flex h-screen">
      {/* Settings Sidebar */}
      <div className="w-80 border-r border-border bg-muted/30 relative">
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-1">
            {mode === 'personal' ? 'Settings' : 'Organisation Settings'}
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            {mode === 'personal' ? 'Your preferences and account' : 'Manage workspace configuration'}
          </p>

          <nav className="space-y-1">
            {settingsMenuItems.filter((i) => i.group === mode).map(renderItem)}
          </nav>
        </div>
        
        {/* Help card removed - the documentation link was broken. */}
      </div>

      {/* Settings Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6">
          {renderSettingsContent()}
        </div>
      </div>
    </div>
  );
}